import { ref } from 'vue'
import { MicVAD } from '@ricky0123/vad-web'

const VAD_ASSET_BASE_PATH = `${process.env.BASE_URL || '/'}vad/`

export function useVadSpeechRecorder({
  onSpeechStart,
  onSpeechEnd,
  onVADMisfire,
  onFrameProcessed,
  getStream,
} = {}) {
  const vad = ref(null)
  const isReady = ref(false)
  const isPrepared = ref(false)
  const isListening = ref(false)
  let hasStarted = false

  async function init() {
    if (vad.value) return vad.value

    vad.value = await MicVAD.new({
      model: 'v5',
      startOnLoad: false,
      baseAssetPath: VAD_ASSET_BASE_PATH,
      onnxWASMBasePath: VAD_ASSET_BASE_PATH,
      positiveSpeechThreshold: 0.45,
      negativeSpeechThreshold: 0.3,
      redemptionMs: 900,
      preSpeechPadMs: 500,
      minSpeechMs: 120,
      submitUserSpeechOnPause: true,
      // 暂停时只断开音频节点、不关闭麦克风；恢复时复用同一路流，避免每句都重新申请权限。
      // 流的释放由调用方统一负责。
      ...(getStream ? {
        getStream,
        pauseStream: async () => {},
        resumeStream: () => getStream(),
      } : {}),
      onSpeechStart: () => onSpeechStart?.(),
      onSpeechEnd: (audio) => onSpeechEnd?.(audio),
      onVADMisfire: (audio) => onVADMisfire?.(audio),
      onFrameProcessed: (probabilities, frame) => onFrameProcessed?.(probabilities, frame),
    })

    isReady.value = true
    return vad.value
  }

  async function start() {
    const instance = await init()
    await instance.start()
    if (instance.errored || !instance.listening) {
      throw new Error(instance.errored || 'Microphone did not start')
    }
    hasStarted = true
    isPrepared.value = true
    isListening.value = true
  }

  async function prepare() {
    if (isPrepared.value) return
    await start()
    await pause()
  }

  async function pause() {
    if (!vad.value) return
    await vad.value.pause()
    isListening.value = false
  }

  // How long a silence the VAD itself tolerates before it ends the speech.
  function setRedemptionMs(redemptionMs) {
    vad.value.setOptions({ redemptionMs })
  }

  async function destroy() {
    if (vad.value && hasStarted) {
      await vad.value.destroy()
    } else {
      await pause()
    }
    vad.value = null
    isReady.value = false
    isPrepared.value = false
    isListening.value = false
    hasStarted = false
  }

  return {
    isReady,
    isPrepared,
    isListening,
    init,
    prepare,
    start,
    pause,
    setRedemptionMs,
    destroy,
  }
}
